// ══════════════════════════════════════════════════════════════════
// utils.js — La Conciergerie
// Fonctions utilitaires partagées (date, format, geo, photo, UI)
// Extrait du Script 1 de index.html (migration Option A → Option B Vite)
// ══════════════════════════════════════════════════════════════════

import { PAUSE_MIN_DURATION_MS, PAUSE_REMINDER_MS, PAUSE_REQUIRED_MS } from './constants.js';

// ══════════════ DATE & FORMAT & MÉTIER ══════════════
// ══════════════ UTILS ══════════════
// today() et tp() utilisent la DATE LOCALE (timezone du navigateur).
// Sans ça, après minuit en France l'app pensait être encore la veille (UTC en retard de 1-2h sur l'heure française).
// Détecte le fournisseur PMS depuis l'URL d'export iCal renseignée sur la fiche bien.
// Retourne 'eviivo' | 'beds24' | null (URL vide ou non reconnue). Ajout de domaine futur :
// un seul endroit à toucher (migration Eviivo→Beds24, v161+).
export const detectPmsVendor=url=>{
  if(!url)return null;
  if(/^https:\/\/io\.eviivo\.com\//.test(url))return'eviivo';
  if(/^https:\/\/beds24\.com\//.test(url))return'beds24';
  return null;
};
export const pmsLabel=vendor=>vendor==='beds24'?'Beds24':vendor==='eviivo'?'Eviivo':'PMS';

export const today=()=>{const d=new Date();d.setMinutes(d.getMinutes()-d.getTimezoneOffset());return d.toISOString().slice(0,10);};
export const tp=n=>{const d=new Date();d.setDate(d.getDate()+n);d.setMinutes(d.getMinutes()-d.getTimezoneOffset());return d.toISOString().slice(0,10);};
export const now=()=>new Date().toISOString();
export const ft=iso=>iso?new Date(iso).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}):'—';
export const fd=iso=>iso?new Date(iso).toLocaleDateString('fr-FR',{weekday:'short',day:'numeric',month:'short'}):'—';
export const fm=(m,y)=>['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'][m]+' '+y;
export const fdur=ms=>{if(!ms||ms<0)return'—';const m=Math.floor(ms/60000);return`${Math.floor(m/60)}h${String(m%60).padStart(2,'0')}`;};
export const feur=n=>n!=null?`${Number(n).toFixed(2).replace('.',',')} €`:'—';
// Description courte et lisible d'une absence (pour le journal des modifications).
// Tolère l'absence de fd() côté date (utilise un format simple yyyy-mm-dd → jj/mm/aaaa).
export const _fdShort=s=>{if(!s)return'?';const p=String(s).split('-');return p.length===3?`${p[2]}/${p[1]}/${p[0]}`:String(s);};
export const leaveLabel=l=>{
  if(!l)return'—';
  if(l.type==='indispo'){
    const det=l.indispoType==='creneau'?`indispo ${l.indispoStart||'?'}–${l.indispoEnd||'?'}`:l.indispoType==='debut'?`dispo dès ${l.indispoStart||'?'}`:`dispo jusqu'à ${l.indispoEnd||'?'}`;
    return `Indispo ${_fdShort(l.sd)} (${det})`;
  }
  if(l.type==='repos')return `Repos hebdo ${_fdShort(l.sd)}`;
  if(l.type==='recup')return `Récup ${_fdShort(l.sd)} ${l.demi==='am'?'matin':'après-midi'}`;
  if(l.type==='demi')return `Demi-journée ${_fdShort(l.sd)} ${l.demi==='am'?'matin':'après-midi'}`;
  return `Congés du ${_fdShort(l.sd)} au ${_fdShort(l.ed)}`;
};
// Construit une entrée de journal {at,by,role,from,to} si la demande a réellement changé.
// Compare les champs significatifs ; retourne null si rien n'a changé.
export const leaveHistoryEntry=(oldL,newL,by,role)=>{
  const keys=['type','sd','ed','demi','indispoType','indispoStart','indispoEnd','reason'];
  const changed=keys.some(k=>(oldL?.[k]??'')!==(newL?.[k]??''));
  if(!changed)return null;
  return {at:now(),by:by||'?',role:role||'?',from:leaveLabel(oldL),to:leaveLabel(newL)};
};
// Affichage nom complet d'une personne : "Prénom Nom" si firstName, sinon juste "Nom"
export const fname=p=>{if(!p)return'?';const fn=(p.firstName||'').trim(),ln=(p.name||'').trim();return fn?`${fn} ${ln}`.trim():ln||'?';};
// Forme courte "Prénom N." — utilisée sur les plannings (badges/bandeaux binôme) pour gagner de la place.
export const fnameShort=p=>{if(!p)return'?';const fn=(p.firstName||'').trim(),ln=(p.name||'').trim();if(!fn)return ln||'?';return ln?`${fn} ${ln[0].toUpperCase()}.`:fn;};

// Mode guidé formation — deux axes séparés : degré d'imposition (off/optin/suggere/obligatoire) et liberté
// dans le guide une fois dedans (souple/strict). Rétrocompat : les salariés jamais réédités depuis l'ajout
// de guideImposition n'ont que l'ancien champ guideMode (bool legacy true / 'off' / 'souple' / 'strict'),
// qui à l'époque mélangeait les deux notions — 'souple'/'strict' activés valaient déjà une imposition totale.
export const guideImposition=s=>{
  if(s?.guideImposition)return s.guideImposition;
  return(s?.guideMode===true||s?.guideMode==='strict'||s?.guideMode==='souple')?'obligatoire':'off';
};
export const guideFreedom=s=>s?.guideMode==='souple'?'souple':'strict';
// Statut effectif d'une tâche : prend en compte les pointages (als) en priorité sur t.st
// → 'done' si pointage 'end', 'progress' si pointage 'start' sans 'end', sinon t.st (par défaut 'todo')
export const effSt=(t,als)=>{
  if(!t)return'todo';
  if(als&&Array.isArray(als)){
    const hasEnd=als.some(l=>l.tid===t.id&&l.type==='end');
    if(hasEnd)return'done';
    const hasStart=als.some(l=>l.tid===t.id&&l.type==='start');
    if(hasStart)return'progress';
  }
  return t.st||'todo';
};
// Pauses : un pointage tc peut avoir des pauses dans son tableau `breaks: [{start, end}, ...]`
// pour la compatibilité, on garde aussi sb/eb (première pause "legacy"). Helpers centralisés ci-dessous.
// Renvoie la liste complète des pauses d'un pointage (legacy sb/eb + breaks[]), en ms
export const getBreaks=c=>{
  if(!c)return[];
  const out=[];
  if(c.sb&&c.eb)out.push({start:c.sb.ts,end:c.eb.ts,_admin:c.sb._admin||c.eb._admin});
  else if(c.sb&&!c.eb)out.push({start:c.sb.ts,end:null,_admin:c.sb._admin});
  if(Array.isArray(c.breaks))c.breaks.forEach(b=>{if(b?.start)out.push({start:b.start,end:b.end||null,_admin:b._admin});});
  return out;
};
// Total des pauses en ms (uniquement les pauses terminées)
export const totalBreakMs=c=>getBreaks(c).reduce((s,b)=>s+(b.end?Math.max(0,new Date(b.end)-new Date(b.start)):0),0);
// ── Analyse des écarts d'une journée (v164, repère v164.2) ──
// Décompose une journée pointée (début→fin) en segments chronologiques :
// intervention (ménage d'appartement), pause (déclarée via ☕, breaks[]), écart (ni l'un ni l'autre).
// Fonction pure, lecture seule — utilisée par la modale d'analyse (SecRH) et la section
// "Résumé des écarts" du PDF journalier (generateHistoryPdf). Ne calcule et n'affiche jamais
// de seuil ni de jugement automatique (décision explicite de Claire, session du 17/08/2026) :
// le classement pause/intervention/écart est factuel, l'interprétation reste humaine.
// Chaque segment "écart" porte un champ `repere` (v164.2, retour de Claire après la
// première livraison : le tableau perdait l'info "entre quels appartements ?", qui rendait
// l'analyse manuelle facile à lire) — repère purement factuel (les deux bornes voisines du
// trou), PAS une hypothèse sur sa nature (trajet/pause/autre) : ça, ça reste écarté (§2 v164).
// Retourne null si la journée n'est pas close (pas de c.ew) — l'analyse n'a de sens que sur
// une journée terminée, faute de quoi le dernier segment "écart" serait indéfiniment ouvert.
export const ecartsJournee=(c,dayAls,biens)=>{
  if(!c?.sw||!c?.ew)return null;
  const sorted=(dayAls||[]).filter(l=>!l.archived).sort((a,b)=>a.ts.localeCompare(b.ts));
  const byTid={};
  sorted.forEach(l=>{if(!byTid[l.tid])byTid[l.tid]={tid:l.tid,bid:l.bid};byTid[l.tid][l.type]=l;});
  const interventions=Object.values(byTid).filter(g=>g.start&&g.end).map(g=>{
    const b=biens?.find(x=>x.id===g.bid);
    return{type:'intervention',label:b?.name||'Appartement',start:g.start.ts,end:g.end.ts};
  });
  const pauses=getBreaks(c).filter(b=>b.end).map((b,i)=>({type:'pause',label:`Pause ${i+1}`,start:b.start,end:b.end}));
  const occupied=[...interventions,...pauses].sort((a,b)=>a.start.localeCompare(b.start));
  const segments=[];
  let cursor=c.sw.ts;
  occupied.forEach(seg=>{
    if(seg.start>cursor)segments.push({type:'ecart',label:'Hors intervention',start:cursor,end:seg.start});
    segments.push(seg);
    if(seg.end>cursor)cursor=seg.end;
  });
  if(c.ew.ts>cursor)segments.push({type:'ecart',label:'Hors intervention',start:cursor,end:c.ew.ts});
  const withDur=segments.map(s=>({...s,ms:Math.max(0,new Date(s.end)-new Date(s.start))})).filter(s=>s.ms>0);
  const neighborLabel=seg=>!seg?null:seg.type==='intervention'?seg.label:seg.type==='pause'?'pause':null;
  return withDur.map((s,i)=>{
    if(s.type!=='ecart')return s;
    const prevLbl=neighborLabel(withDur[i-1]);
    const nextLbl=neighborLabel(withDur[i+1]);
    let repere;
    if(prevLbl&&nextLbl)repere=`${prevLbl} → ${nextLbl}`;
    else if(nextLbl)repere=`Avant ${nextLbl}`;
    else if(prevLbl)repere='Fin de journée';
    else repere='Journée entière';
    return{...s,repere};
  });
};
// ── Pauses : fermeture automatique au démarrage d'un appartement (v130) ──
// Une pause ouverte au bouton ☕ n'était refermée par rien : ni la Fin de l'appartement
// en cours, ni le Début du suivant. Elle restait ouverte indéfiniment, comptait 0 dans
// totalBreakMs (donc payée), et devenait INFERMABLE dès qu'une autre pause était ajoutée
// après elle — breakInProgress() et punch('eb') ne regardent que le DERNIER élément du
// tableau. C'est exactement ce qui s'est produit le 03/08/2026 sur le pointage de Lina.
// Règle retenue (validée Claire) : le ▶ Début d'un appartement ferme la pause ouverte,
// en BORNANT son début à la dernière fin d'appartement — sans ce bornage, une pause
// ouverte pendant un appartement encore en cours ferait décompter du temps où la
// salariée était pointée en train de travailler.
// prevEndTs : horodatage de la dernière fin d'appartement antérieure, ou null (aucune).
// Fonction PURE : ne mute jamais l'entrée. Renvoie le tableau d'origine (même
// référence) s'il n'y a rien à fermer — l'appelant peut tester l'égalité de référence.
export const fermerPauseOuverte=(breaks,prevEndTs,newStartTs)=>{
  const arr=Array.isArray(breaks)?breaks:[];
  const last=arr.length-1;
  if(last<0||!arr[last]?.start||arr[last].end)return arr; // aucune pause ouverte
  const fin=new Date(newStartTs).getTime();
  const deb=new Date(arr[last].start).getTime();
  const borne=prevEndTs?new Date(prevEndTs).getTime():null;
  let start=(borne!=null&&borne>deb)?borne:deb;
  // Cas dégénéré (borne ou début postérieurs au ▶ Début) : pause de 0 min, CONSERVÉE
  // quand même — append-only, on ne supprime jamais la trace du tap ☕.
  if(start>fin)start=fin;
  const out=arr.slice();
  const b={...arr[last],start:start===deb?arr[last].start:new Date(start).toISOString(),end:new Date(fin).toISOString(),_autoClosed:true};
  if(start!==deb)b._startOrig=arr[last].start; // trace du bornage, pour l'audit
  out[last]=b;
  return out;
};
// Minutes de pause déjà déclarées à l'intérieur de la fenêtre [fromTs, toTs].
// Une pause encore ouverte est comptée jusqu'à toTs. Sert à la détection de « pause
// oubliée » (encadrement renforcé) : sans ça, l'appli redemande une pause que la
// salariée a déjà pointée elle-même, et l'enregistre une seconde fois.
// Fonction PURE.
export const pausesDansFenetre=(breaks,fromTs,toTs)=>{
  const f=new Date(fromTs).getTime(),t=new Date(toTs).getTime();
  if(!(t>f))return 0;
  return(Array.isArray(breaks)?breaks:[]).reduce((s,b)=>{
    if(!b?.start)return s;
    const d=new Date(b.start).getTime();
    const e=b.end?new Date(b.end).getTime():t; // pause ouverte → comptée jusqu'à la borne haute
    const inter=Math.min(t,e)-Math.max(f,d);
    return s+(inter>0?inter:0);
  },0)/60000;
};
// ── tc7 : application d'une modification sur l'enregistrement du jour (v134) ──
// Contrairement à als7/controles7 (un événement atomique par entrée, simple
// « déjà présent ? sinon ajouter »), tc7 a UN SEUL enregistrement par salarié-jour,
// modifié EN PLACE à chaque pointage (début, pause, fin). Toute écriture doit donc
// partir d'un tableau FRAIS (lu juste avant), jamais du state local en mémoire —
// sans ça, une écriture concurrente (autre onglet, correction admin pendant que
// l'app était fermée) est silencieusement écrasée. Incident déclencheur : la pause
// déclarée par Lina le 04/08/2026 n'est jamais apparue dans son pointage.
// Fonction PURE : ne mute jamais l'entrée. `update(record)` doit elle-même respecter
// la convention « même référence = rien à faire » (comme fermerPauseOuverte) pour que
// l'appelant puisse détecter une écriture no-op et s'abstenir d'un réseau inutile.
// `makeNew` (optionnel) ne sert qu'au tout premier pointage du jour ; si absent et
// qu'aucun enregistrement n'existe pour {sid,date}, le tableau est renvoyé inchangé
// (même référence) — l'appelant doit alors traiter ça comme une anomalie, pas un no-op
// silencieux ordinaire, car dans tous les sites d'appel actuels le jour est censé déjà
// avoir commencé.
export const applyTcUpdate=(freshTc,sid,date,{makeNew,update}={})=>{
  const arr=Array.isArray(freshTc)?freshTc:[];
  const i=arr.findIndex(c=>c.sid===sid&&c.date===date);
  if(i<0)return makeNew?[...arr,makeNew()]:arr;
  const updated=update(arr[i]);
  if(updated===arr[i])return arr; // update() signale « rien à faire »
  return arr.map((c,idx)=>idx===i?updated:c);
};
// ── Marquage d'un contrôle sur son rapport (v130) ──
// Quand un contrôle est clos, le rapport visé (`ctrl.repId`) reçoit ctrlId/ctrlVerdict/
// ctrlAt. C'était fait inline dans saveCtrl (SecRapports.jsx) et NULLE PART ailleurs :
// `enqueueControle` protège le contrôle lui-même contre une coupure réseau, mais pas ce
// marquage, et `autoReplayControles7` ne le rejouait pas. Un contrôle récupéré après une
// panne laissait donc son rapport durablement affiché comme « non contrôlé ».
// `ecraser` distingue les deux intentions, et ce n'est PAS un détail :
//   • true  → saveCtrl, contrôle validé à l'instant : un recontrôle doit remplacer
//             le verdict précédent.
//   • false → rejeu, contrôle possiblement vieux de plusieurs jours : ne jamais écraser
//             un AUTRE contrôle déjà marqué, sinon un rejeu tardif ferait régresser un
//             rapport recontrôlé entre-temps vers un vieux verdict.
// Seuls les verdicts définitifs marquent ('reporter'/'abandonne' ne marquent rien).
// Fonction PURE : renvoie le tableau d'origine (même référence) si rien ne change.
export const appliquerMarquageControle=(rep,ctrl,{ecraser=false}={})=>{
  const arr=Array.isArray(rep)?rep:[];
  if(!ctrl||!ctrl.repId)return arr;
  if(ctrl.verdict!=='pret'&&ctrl.verdict!=='nonpret')return arr;
  const at=ctrl.closedAt||null;
  let touched=false;
  const out=arr.map(r=>{
    if(!r||r.id!==ctrl.repId)return r;
    // Rejeu tardif : un autre contrôle a déjà marqué ce rapport → on n'y touche pas.
    if(!ecraser&&r.ctrlId&&r.ctrlId!==ctrl.id)return r;
    if(r.ctrlId===ctrl.id&&r.ctrlVerdict===ctrl.verdict&&(r.ctrlAt||null)===at)return r; // déjà à jour
    touched=true;
    return{...r,ctrlId:ctrl.id,ctrlVerdict:ctrl.verdict,ctrlAt:at};
  });
  return touched?out:arr;
};
// Temps de travail effectif (du début de poste à la fin de poste, moins toutes les pauses)
export const totalWorkMs=c=>{if(!c?.sw||!c?.ew)return null;const t=new Date(c.ew.ts)-new Date(c.sw.ts);return Math.max(0,t-totalBreakMs(c));};
// Y a-t-il une pause en cours (commencée mais pas terminée) ?
export const breakInProgress=c=>{const bs=getBreaks(c);return bs.length>0&&!bs[bs.length-1].end;};
// État réglementaire des pauses pour un pointage en cours.
// Retourne { status: 'ok'|'warning'|'overdue'|'compliant', workMs, longBreakMs }
// - 'ok' : a déjà fait une pause ≥30 min, conforme
// - 'compliant' : pas encore au seuil de rappel
// - 'warning' : >5h30 sans pause valide → bannière jaune
// - 'overdue' : >6h sans pause valide → bannière rouge (dépassement légal)
export const pauseStatus=c=>{
  if(!c?.sw)return{status:'compliant',workMs:0,longBreakMs:0};
  const ew=c.ew?new Date(c.ew.ts):new Date();
  const onBreak=breakInProgress(c);
  // Si en pause maintenant, on fige le calcul à l'heure du début de la pause en cours
  const bs=getBreaks(c);
  const referenceTime=onBreak&&bs.length>0?new Date(bs[bs.length-1].start):ew;
  const totalMs=referenceTime-new Date(c.sw.ts);
  const breakTotalMs=totalBreakMs(c);
  const workMs=Math.max(0,totalMs-breakTotalMs);
  // Y a-t-il une pause terminée d'au moins 30 min ?
  const longBreakMs=bs.filter(b=>b.end).reduce((max,b)=>{const d=new Date(b.end)-new Date(b.start);return d>max?d:max;},0);
  const hasValidBreak=longBreakMs>=PAUSE_MIN_DURATION_MS;
  if(hasValidBreak)return{status:'ok',workMs,longBreakMs};
  if(onBreak)return{status:'compliant',workMs,longBreakMs};
  if(workMs>=PAUSE_REQUIRED_MS)return{status:'overdue',workMs,longBreakMs};
  if(workMs>=PAUSE_REMINDER_MS)return{status:'warning',workMs,longBreakMs};
  return{status:'compliant',workMs,longBreakMs};
};
export const gMon=d=>{const x=new Date(d);const dy=x.getDay();x.setDate(x.getDate()+(dy===0?-6:1-dy));x.setHours(0,0,0,0);return x;};
export const aD=(d,n)=>{const x=new Date(d);x.setDate(x.getDate()+n);return x;};
export const iD=d=>{const x=new Date(d);x.setMinutes(x.getMinutes()-x.getTimezoneOffset());return x.toISOString().slice(0,10);};
// ── Helpers semaine ISO (Morceau 2 : facturation prestations a la semaine) ──
// Lundi (YYYY-MM-DD) de la semaine contenant dateStr. Robuste timezone (midi local).
export const lundiDeISO=dateStr=>{if(!dateStr)return'';const d=new Date(dateStr+'T12:00:00');const j=(d.getDay()+6)%7;d.setDate(d.getDate()-j);return iD(d);};
// Numero de semaine ISO 8601 (1..53) d'une date YYYY-MM-DD.
export const numSemaineISO=dateStr=>{if(!dateStr)return 0;const d=new Date(dateStr+'T12:00:00');const j=(d.getDay()+6)%7;d.setDate(d.getDate()-j+3);const premierJeudi=new Date(d.getFullYear(),0,4);const pj=(premierJeudi.getDay()+6)%7;premierJeudi.setDate(premierJeudi.getDate()-pj+3);return 1+Math.round((d-premierJeudi)/(7*864e5));};
// Bornes [lundi, dimanche] (YYYY-MM-DD) a partir d'un lundi.
export const bornesSemaine=lundiStr=>{if(!lundiStr)return['',''];const d=new Date(lundiStr+'T12:00:00');const dim=new Date(d);dim.setDate(d.getDate()+6);return[iD(d),iD(dim)];};
// Libelle ASCII court d'un jour : "Lun 09/06" (pour la colonne Jour de la facture, jsPDF sans accent).
export const JOUR_ASCII=['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
export const labelJourASCII=dateStr=>{if(!dateStr)return'';const d=new Date(dateStr+'T12:00:00');const j=(d.getDay()+6)%7;return JOUR_ASCII[j]+' '+String(d.getDate()).padStart(2,'0')+'/'+String(d.getMonth()+1).padStart(2,'0');};
export const distM=(la1,lo1,la2,lo2)=>{const R=6371000,f1=la1*Math.PI/180,f2=la2*Math.PI/180,df=(la2-la1)*Math.PI/180,dl=(lo2-lo1)*Math.PI/180,a=Math.sin(df/2)**2+Math.cos(f1)*Math.cos(f2)*Math.sin(dl/2)**2;return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));};
export const geoOk=(geo,p)=>{if(!geo||!p?.lat)return{ok:null,d:null};const d=Math.round(distM(geo.lat,geo.lng,p.lat,p.lng));return{ok:d<=(p.gr||150),d};};

// ── Facturation intervention a l'heure (Chantier C) ──
// Duree facturable (minutes) d'une tache 'intervention' :
//  1) temps brut = end.ts - start.ts (pointages als de la tache), ou t.dureeManuelle si pas de fin pointee
//  2) minimum 1h (60 min) ferme
//  3) arrondi au quart d'heure superieur (ceil sur 15 min)
// Retourne { minutes, source:'pointage'|'manuel'|'aucun', brut } — minutes=null si rien d'exploitable.
export const dureeFacturableIntervention=(t,als)=>{
  if(!t)return{minutes:null,source:'aucun',brut:null};
  let brut=null,source='aucun';
  if(Array.isArray(als)){
    const st=als.find(l=>l.tid===t.id&&l.type==='start');
    const en=als.find(l=>l.tid===t.id&&l.type==='end');
    if(st&&en){brut=Math.max(0,(new Date(en.ts)-new Date(st.ts))/60000);source='pointage';}
  }
  if(brut===null&&Number(t.dureeManuelle)>0){brut=Number(t.dureeManuelle);source='manuel';}
  if(brut===null)return{minutes:null,source:'aucun',brut:null};
  const minutes=Math.max(60,Math.ceil(brut/15)*15);
  return{minutes,source,brut:Math.round(brut)};
};
// Montant HT d'une intervention = (minutes/60) * tarifInterventionH du bien
// ── Correspondance item de rapport → supplément facturable (v152) ──
// Claire : les salariées cochent un point du rapport (ex. « Spa nettoyé », qui peut être
// n'importe où — pas forcément dans une pièce dédiée, chez elle c'est dans « Salle de bain »)
// sans cocher le supplément correspondant à l'étape Facturation. Correspondance EXPLICITE au
// niveau de l'ITEM (`item.billKey`, jamais déduite d'un nom de pièce ou d'un libellé) — même
// principe que `factuKey` côté devis (manuel, décision 17) : jamais d'automatisme qui devine.
// `item.billKey` est réglé par Claire dans l'éditeur du bien et matché au `key` d'un item `bf`
// (bien.bf[].key, posé quand l'item est ajouté depuis la bibliothèque BILL_TYPES).
// Retourne les suppléments "signalés" (item coché) mais dont la case bf n'est pas cochée.
// Un item lié à une clé sans supplément configuré sur CE bien est silencieusement ignoré
// (rien à vérifier, pas une anomalie).
export const supplementsNonCoches=(items,cks,bien,bill)=>{
  const bfArr=Array.isArray(bien?.bf)?bien.bf:[];
  const manquants=[];
  (items||[]).forEach(it=>{
    if(!it.billKey||!cks[it.id])return;
    const bf=bfArr.find(f=>f.key===it.billKey);
    if(!bf)return;
    const v=(bill||{})[bf.id];
    const coche=bf.t==='n'?(Number(v)||0)>0:v===true;
    if(!coche)manquants.push({billKey:it.billKey,itemLb:it.lb,bfLb:bf.lb});
  });
  return manquants;
};

export const montantInterventionHT=(t,als,bien)=>{
  const{minutes}=dureeFacturableIntervention(t,als);
  if(minutes===null)return null;
  const th=Number(bien?.tarifInterventionH||0);
  return Math.round((minutes/60)*th*100)/100;
};

// ── Pointage brut d'une tache de menage (PDF contrôle, v150) ──
// Contrairement a dureeFacturableIntervention (facturation horaire), ici on veut
// le temps REEL pointe, sans plancher 60min ni arrondi au quart d'heure — c'est de
// l'information, pas un montant a facturer. Retourne {startTs,endTs,ms} — ms=null
// si le pointage est incomplet ou absent (controle direct sans tache liee, ancien
// historique, etc.) : a l'appelant d'afficher '—' proprement.
export const pointageTache=(tid,als)=>{
  if(!tid||!Array.isArray(als))return{startTs:null,endTs:null,ms:null};
  const st=als.find(l=>l.tid===tid&&l.type==='start');
  const en=als.find(l=>l.tid===tid&&l.type==='end');
  if(!st||!en)return{startTs:st?.ts||null,endTs:en?.ts||null,ms:null};
  return{startTs:st.ts,endTs:en.ts,ms:Math.max(0,new Date(en.ts)-new Date(st.ts))};
};

// ── Seuils qualité (durée min/max) par bien+type de ménage (v154) ──
// Champs DÉDIÉS sur le bien : dureeMin{Full,Quick,Linge} / dureeMax{Full,Quick,Linge}
// (minutes), volontairement indépendants de dm{Full,Quick,Linge} (durée planning par
// défaut, sert uniquement à préremplir t.dm à la création de tâche) — modifier l'un
// ne doit jamais faire bouger l'autre. Vide/0 sur un bord = pas de contrainte sur ce
// bord (rétrocompat totale : aucun bien existant n'est concerné tant que Claire n'a
// rien rempli). Demande directe de Claire (10/08/2026) : bloquer les ménages trop
// courts ET trop longs, avec un même mécanisme de signalement des deux côtés.
export const dureeSeuils=(bien,type)=>{
  const suffix=type==='full'?'Full':type==='quick'?'Quick':type==='linge'?'Linge':null;
  if(!bien||!suffix)return{min:null,max:null};
  const min=Number(bien['dureeMin'+suffix])||0;
  const max=Number(bien['dureeMax'+suffix])||0;
  return{min:min>0?min:null,max:max>0?max:null};
};
// Statut d'une tâche de ménage EN COURS par rapport au seuil configuré sur son bien :
// 'sous' (encore trop tôt pour clôturer), 'dessus' (déjà au-delà du max), null (dans
// la fourchette, ou aucun seuil configuré sur ce bien+type — comportement inchangé).
// elapsedMin = minutes écoulées depuis le pointage Début. Volontairement à la charge
// de l'appelant (pas de lecture d'horloge ici) — fonction pure, testable sans mock Date.
export const dureeStatut=(bien,type,elapsedMin)=>{
  const{min,max}=dureeSeuils(bien,type);
  if(min!=null&&elapsedMin<min)return'sous';
  if(max!=null&&elapsedMin>max)return'dessus';
  return null;
};

// Formatage lisible d'une taille de fichier en octets (pièces jointes messagerie).
export const formatFileSize=bytes=>{
  const n=Number(bytes);
  if(!n||n<=0)return'';
  if(n<1024)return n+' o';
  if(n<1024*1024)return Math.round(n/1024)+' Ko';
  return (n/(1024*1024)).toFixed(1)+' Mo';
};

// ══════════════ PHOTO & COMPRESSION ══════════════
// Estimation rapide de la taille d'une dataURL base64 en KB
export const dataUrlSizeKB=u=>typeof u==='string'&&u.startsWith('data:')?Math.round(u.length*0.75/1024):0;

export async function compressToDataUrl(input,opts={}){
  const{maxDim=1280,quality=0.7}=opts;
  // Convertit un Blob en dataURL (utilisé seulement à la toute fin, sur le blob DÉJÀ compressé et petit)
  const blobToDataUrl=b=>new Promise((res,rej)=>{const r=new FileReader();r.onload=e=>res(e.target.result);r.onerror=()=>rej(new Error('FileReader failed'));r.readAsDataURL(b);});

  // ─── Chemin mémoire-safe : entrée Blob/File + APIs modernes disponibles ───
  if(input instanceof Blob && typeof createImageBitmap==='function'){
    let bitmap=null,canvas=null;
    try{
      // Décodage redimensionné quand c'est supporté (Chrome Android) : le décodeur produit
      // directement un petit bitmap au lieu de matérialiser le plein format (ex. 50 Mpx) en mémoire.
      try{
        const probe=await createImageBitmap(input);
        const pw=probe.width,ph=probe.height;
        if(probe.close)probe.close();
        const pr=Math.min(maxDim/Math.max(pw,ph),1);
        bitmap=await createImageBitmap(input,{resizeWidth:Math.max(1,Math.round(pw*pr)),resizeHeight:Math.max(1,Math.round(ph*pr)),resizeQuality:'high'});
      }catch(_){
        bitmap=await createImageBitmap(input);
      }
      const w=bitmap.width,h=bitmap.height;
      const ratio=Math.min(maxDim/Math.max(w,h),1);
      const newW=Math.max(1,Math.round(w*ratio)),newH=Math.max(1,Math.round(h*ratio));
      canvas=document.createElement('canvas');
      canvas.width=newW;canvas.height=newH;
      const ctx=canvas.getContext('2d');
      ctx.drawImage(bitmap,0,0,newW,newH);
      // Libère le bitmap source (gros) dès qu'il est dessiné
      if(bitmap.close)bitmap.close();
      bitmap=null;
      // toBlob : pas de grosse chaîne base64 en mémoire (contrairement à toDataURL)
      const outBlob=await new Promise((res,rej)=>{
        if(!canvas.toBlob){rej(new Error('toBlob non supporté'));return;}
        canvas.toBlob(b=>b?res(b):rej(new Error('toBlob a renvoyé null')),'image/jpeg',quality);
      });
      const dataUrl=await blobToDataUrl(outBlob);
      // Libère le canvas
      canvas.width=0;canvas.height=0;
      return dataUrl;
    }catch(e){
      // Nettoyage avant de retomber sur l'ancienne méthode
      try{if(bitmap&&bitmap.close)bitmap.close();}catch(_){}
      try{if(canvas){canvas.width=0;canvas.height=0;}}catch(_){}
      console.warn('[compressToDataUrl] chemin createImageBitmap/toBlob indisponible, fallback ancienne méthode:',e&&e.message);
      // on continue vers l'ancien chemin ci-dessous
    }
  }

  // ─── Ancien chemin (fallback) : dataURL en entrée, ou createImageBitmap/toBlob indisponibles ───
  let sourceUrl;
  if(input instanceof Blob){
    sourceUrl=await blobToDataUrl(input);
  }else{
    sourceUrl=input;
  }
  return new Promise((resolve,reject)=>{
    const img=new Image();
    img.onload=()=>{
      const w=img.width,h=img.height;
      const ratio=Math.min(maxDim/Math.max(w,h),1);
      const newW=Math.round(w*ratio),newH=Math.round(h*ratio);
      const canvas=document.createElement('canvas');
      canvas.width=newW;canvas.height=newH;
      const ctx=canvas.getContext('2d');
      ctx.drawImage(img,0,0,newW,newH);
      const out=canvas.toDataURL('image/jpeg',quality);
      canvas.width=0;canvas.height=0; // libère le canvas
      resolve(out);
    };
    img.onerror=()=>reject(new Error('Image load failed'));
    img.src=sourceUrl;
  });
}

// Estimation rapide de la taille d'une dataURL base64 en KB

// Convertit un timestamp ISO en valeur d'input datetime-local (local time)
export const tsToLocal=ts=>{if(!ts)return'';const d=new Date(ts);const o=d.getTimezoneOffset()*60000;return new Date(d-o).toISOString().slice(0,16);};
export const localToTs=loc=>loc?new Date(loc).toISOString():null;

// Lit un fichier image et retourne une dataURL (base64). Limite à 1.5 Mo après compression.
export const fileToDataUrl=(file,maxKB=1500)=>{
  if(file instanceof Blob){
    return compressToDataUrl(file,{maxDim:1280,quality:0.7})
      .then(async u=>{let q=0.7;while(dataUrlSizeKB(u)>maxKB&&q>0.4){q-=0.1;u=await compressToDataUrl(file,{maxDim:1280,quality:q});}return u;});
  }
  return new Promise((res,rej)=>{
  const fr=new FileReader();
  fr.onload=()=>{
    const img=new Image();
    img.onload=()=>{
      const max=1280;let w=img.width,h=img.height;
      if(w>max||h>max){if(w>h){h=Math.round(h*max/w);w=max;}else{w=Math.round(w*max/h);h=max;}}
      const cv=document.createElement('canvas');cv.width=w;cv.height=h;
      cv.getContext('2d').drawImage(img,0,0,w,h);
      let q=0.85;let url=cv.toDataURL('image/jpeg',q);
      while(url.length>maxKB*1024*1.37&&q>0.4){q-=0.1;url=cv.toDataURL('image/jpeg',q);}
      res(url);
    };
    img.onerror=()=>rej('Image illisible');
    img.src=fr.result;
  };
  fr.onerror=()=>rej('Lecture impossible');
  fr.readAsDataURL(file);
});
};

// ═══════════════════════════════════════════════════════════════════
// FIN SCRIPT 1 — SOCLE (constantes, utils, Supabase, useSt, Ic, PDF)
// Découpage modularisation Option A. Tous les scripts partagent le même
// scope global : pas d'import/export. Ordre = socle d'abord, App() en dernier.
// NB : le bloc Contrôle/Rapports (NoteEtoiles→SecControles) reste ici pour
// l'instant, sera rangé en Script 6 ultérieurement (cf. plan v35).
// ═══════════════════════════════════════════════════════════════════

// ─── Prix de la nuitée (source de vérité unique) ───
// Consommé par calcSejour (SecProp.jsx, aperçu de facturation) ET par le calendrier des
// séjours (CalendrierSejours.jsx). Ne JAMAIS recalculer cette formule ailleurs : les deux
// affichages doivent toujours donner le même chiffre au centime.
//
// = (versement voyageur − frais de ménage locataire − taxe si elle est DANS le versement) / nuitées
// La taxe n'est retirée que si elle a été collectée PAR L'OTA (donc présente dans le versement).
// Réglée à l'arrivée : elle n'est jamais passée par l'OTA → on ne la déduit pas.
//
// Utilise `s.nuitees` (champ stocké), et surtout PAS un nombre de nuits recalculé depuis les
// dates : sinon le calendrier afficherait un prix différent de celui de la fiche séjour.
// Renvoie 0 quand le séjour n'est pas renseigné (import non complété) — l'appelant affiche « — ».
export function prixNuitee(s){
  if(!s) return 0;
  const n=Number(s.nuitees||1);
  if(!(n>0)) return 0;
  const taxe=s.nonAssujettiTS?0:Number(s.taxeSejour||0);
  const taxeDansVersement=s.taxeCollecteeParOTA?taxe:0;
  return Math.round((Number(s.versementOTA||0)-Number(s.fraisMenageLocataire||0)-taxeDansVersement)/n*100)/100;
}

// ─── Diagnostic photo (localStorage + heap) ───
export function photoLog(step,extra){
  try{
    const prev=JSON.parse(localStorage.getItem('photo_diag')||'[]');
    const mem=(performance&&performance.memory)?Math.round(performance.memory.usedJSHeapSize/1048576)+'/'+Math.round(performance.memory.jsHeapSizeLimit/1048576)+'Mo':'?';
    prev.push(`[${new Date().toLocaleTimeString('fr-FR')}] ${step}${extra?' · '+extra:''} · heap ${mem}`);
    localStorage.setItem('photo_diag',JSON.stringify(prev.slice(-30)));
  }catch(_){}
}
export function photoLogClear(){try{localStorage.removeItem('photo_diag');}catch(_){}}

// ─── Lot 2 (SPEC_file_locale_v2.md §3) : détection des anomalies de fin de journée ───

// §3.2 — Appartements du jour non pointés (ménages uniquement). Deux cas
// distingués : jamais commencé (aucun pointage start), ou commencé sans fin.
// Pure, testable indépendamment de React/Supabase.
export function calcFinJourneeAptAnomalies(myT,als,todayS){
  const isMen=type=>['full','quick','linge','correction'].includes(type);
  const gL=(tid,type)=>(als||[]).find(l=>l.tid===tid&&l.type===type);
  return (myT||[]).filter(t=>t.date===todayS&&isMen(t.type)).map(t=>{
    const ls=gL(t.id,'start'),le=gL(t.id,'end');
    if(!ls)return{id:t.id,bid:t.bid,reason:'jamais'};
    if(!le)return{id:t.id,bid:t.bid,reason:'nonfini'};
    return null;
  }).filter(Boolean);
}

// §3.1 — Rapports 'fin' du jour encore pending/failed dans la file locale du
// lot 1 (localQueue), pour un salarié et une date donnés. `entries` = résultat
// de `listAll()` (déjà filtré non-confirmed par localQueue.js) ; cette fonction
// ne fait AUCUN accès IndexedDB elle-même — pure, testable sans navigateur.
// Ne compte que fin===true (soumission finale) : un brouillon encore pending
// n'est pas une anomalie de fin de journée, la tâche n'est simplement pas finie.
export function calcRapportsEnAttente(entries,sid,todayS){
  const dateOfTs=ms=>{const d=new Date(ms);d.setMinutes(d.getMinutes()-d.getTimezoneOffset());return d.toISOString().slice(0,10);};
  return (entries||[]).filter(e=>e.key==='rep7'&&e.fin===true&&e.sid===sid&&dateOfTs(e.createdAt)===todayS);
}

// ══════════════ CONTRATS SALARIÉ (chantier contrats[], Étape 1A) ══════════════
// Modèle : SPEC_personnel_contrats_v1.md §1-2. `staff[i].id` est stable À VIE
// (clé de continuité de tc7/rep7/als7/leave/cpSoldes/assist7/pauseQR7…) ;
// `contrats[]` est un tableau append-only de périodes d'emploi, ce qui permet
// enfin de représenter une réembauche comme deux lignes distinctes.
//
// contratsOf(s) est le SEUL point d'entrée — ne jamais lire s.contrats en direct.
//
// ⚠️ ÉTAPE 1C-a — LE REPLI `_legacy` A ÉTÉ RETIRÉ (03/08/2026).
// Jusqu'ici, une fiche sans `contrats[]` mais avec les anciens champs plats
// (`hire`/`ct`/`hr`/`hw`/`wd`/`hm`) se voyait dériver un contrat à la volée, en
// mémoire, pour couvrir la fenêtre entre le déploiement et le passage effectif
// de la migration (App.jsx) sur chaque appareil. Cette fenêtre est fermée : la
// migration tourne en production depuis la v127 et la seule fiche qui lui
// échappait (conditions renseignées, aucune date d'embauche) a été complétée à
// la main par Claire, vérifiée en début de session v129.
//
// Conséquence assumée : une fiche sans `contrats[]` ne produit PLUS aucune
// heure contractuelle, aucun droit à congés, aucun décompte. C'est le
// comportement voulu — mais cela veut dire qu'ajouter un salarié sans passer
// par « ➕ Première embauche » le laisse silencieusement à zéro. Le bandeau
// rouge de `ContratsSection` (« Aucune période d'emploi enregistrée ») est
// désormais le seul garde-fou visible : ne pas le retirer.
//
// ⚠️ NE PAS réintroduire de lecture des champs plats ici ni ailleurs. Ils
// existent encore en base (leur purge est l'Étape 1C-b) mais plus AUCUNE ligne
// de code ne les lit : c'est ce qui rend la purge sans effet sur les chiffres.
//
// ⚠️ GÉRANTS : `gerants1` n'a aucun champ contrat (le formulaire Direction ne
// gère qu'email/téléphone/rôle). contratsOf renvoie donc [] pour un gérant, et
// contratActif/dernierContrat renvoient null. C'est voulu : `allWorkers`
// (SecRH, SecMal, historique pointages) mélange salariés et gérants, et
// `notifBus.js`/`EncadrementRenforce.jsx` filtrent sur un `g.endDate` qui
// appartient au modèle gérant — jamais migré, jamais touché par ce chantier.
export function contratsOf(s){
  if(!s)return[];
  const cs=s.contrats;
  return Array.isArray(cs)&&cs.length?cs:[];
}
// Contrat en vigueur à une DATE donnée ('YYYY-MM-DD'). null si aucun.
// Sert à tout calcul daté (décompte d'un mois passé, coût d'un arrêt maladie,
// heures contractuelles jour par jour…) : c'est le contrat de l'époque qui fait
// foi, jamais le contrat actuel.
export function contratActif(s,dateStr){
  if(!dateStr)return null;
  return contratsOf(s).find(c=>c.hire<=dateStr&&(!c.endDate||dateStr<=c.endDate))||null;
}
// Contrat le plus récent, clos ou non. Sert à l'affichage « conditions
// actuelles » (fiche salarié) et aux filtres qui raisonnent sur la fin du
// dernier engagement connu.
// Écart mineur assumé vs SPEC §2 (qui prend `cs[cs.length-1]`) : on trie sur
// `hire` plutôt que de se fier à l'ordre d'insertion. Résultat identique dans
// le cas normal (append-only ⇒ déjà chronologique), mais une saisie corrective
// insérée dans le désordre ne fait pas silencieusement gagner le mauvais
// contrat — ce qui, sur de la paie, se paie cher.
export function dernierContrat(s){
  const cs=contratsOf(s);
  if(!cs.length)return null;
  return[...cs].sort((a,b)=>String(a.hire||'').localeCompare(String(b.hire||'')))[cs.length-1];
}
// Migration des champs plats vers contrats[] (SPEC §3). Fonction PURE : ne lit
// ni n'écrit rien, l'appelant (App.jsx) fait le read-before-write autour.
// Extraite plutôt que laissée inline pour être testable, comme les fonctions de
// fin de journée du lot 2 (v122).
// Renvoie {updated, changed, count} — `changed:false` ⇒ l'appelant n'écrit rien.
// ⚠️ Étape 1A : les champs plats sont CONSERVÉS. Leur suppression est l'Étape 1C.
export function migrerContrats(list){
  if(!Array.isArray(list))return{updated:list,changed:false,count:0};
  let count=0;
  const updated=list.map(s=>{
    if(!s||typeof s!=='object')return s;
    if(Array.isArray(s.contrats)&&s.contrats.length)return s;  // déjà migré
    if(!s.hire)return s;  // rien d'exploitable (fiche vierge) — jamais de contrat inventé
    count++;
    return{...s,contrats:[{
      id:'ct_migre_'+s.id,
      hire:s.hire,
      endDate:s.endDate||null,
      ct:s.ct||'CDI',
      hr:s.hr,hw:s.hw,wd:s.wd,hm:s.hm,
      motifFin:null,
      note:"Migré automatiquement depuis l'ancien modèle",
    }]};
  });
  return{updated,changed:count>0,count};
}
// Détecte si l'enregistrement d'une fiche salarié constitue une RUPTURE au sens
// du solde de tout compte (Étape 1B-2). Fonction PURE — l'appelant (`saveS`,
// SecRH.jsx) décide seul quoi en faire, et reste le seul à toucher `cpSoldes7`.
// Extraite plutôt que laissée inline : ce chantier a déjà fait tomber trois
// copies divergentes d'une règle laissée dans un composant.
//
// Vrai dans deux cas seulement :
//  (a) une date de fin vient d'être posée sur un contrat qui n'en avait pas ;
//  (b) filet — une réembauche est enregistrée alors que TOUS les contrats
//      précédents étaient déjà clos (rupture saisie avant cette version, dont le
//      solde n'a donc jamais été proposé à la remise à zéro).
// Faux pour un avenant (conditions modifiées, aucune date de fin ajoutée), pour
// une première embauche, et pour un simple changement d'identité.
export function ruptureCP(avant,apres){
  const cAv=Array.isArray(avant?.contrats)?avant.contrats:[];
  const cAp=Array.isArray(apres?.contrats)?apres.contrats:[];
  if(!cAp.length)return false;
  const finPosee=cAp.some(c=>c.endDate&&!cAv.find(x=>x.id===c.id&&x.endDate));
  const reembauche=cAv.length>0&&cAp.length>cAv.length&&cAv.every(c=>c.endDate);
  return finPosee||reembauche;
}

// Découpe un mois calendaire par contrat. Renvoie [{contrat, jours[]}] trié
// chronologiquement, où `jours` = les jours du mois couverts par CE contrat
// (tous les jours, week-ends compris : ce sont les fonctions d'heures qui
// filtrent ensuite). Les jours d'un éventuel TROU entre deux contrats
// (rupture puis réembauche plus tard dans le mois) n'appartiennent à aucune
// entrée — ils ne sont donc comptés nulle part, ce qui est le comportement
// voulu : un solde de tout compte clôt la première période.
// [] si aucun contrat ne couvre le mois (gérant, fiche vierge, mois hors
// période d'emploi).
// Utilisé par le décompte mensuel légal (art. L3171-4) : Claire a tranché
// qu'un mois portant deux contrats donne DEUX décomptes signés séparément,
// jamais un document unique qui mélangerait de l'avant et de l'après-solde.
export function contratsDuMois(s,y,m){
  const last=new Date(y,m+1,0).getDate();
  const map=new Map();
  for(let j=1;j<=last;j++){
    const d=`${y}-${String(m+1).padStart(2,'0')}-${String(j).padStart(2,'0')}`;
    const c=contratActif(s,d);
    if(!c)continue;
    if(!map.has(c.id))map.set(c.id,{contrat:c,jours:[]});
    map.get(c.id).jours.push(d);
  }
  return[...map.values()].sort((a,b)=>String(a.contrat.hire||'').localeCompare(String(b.contrat.hire||'')));
}

// ══════════════ RH — HEURES (contrat/effectif/planifié/absences) ══════════════
// Chantier contrats[] (Étape 0, 02/08/2026) : ces 10 fonctions existaient en
// deux copies — une version VIVANTE et à jour dans SecRH.jsx (utilisée
// partout dans le module RH), et une version MORTE dans AdminUI.jsx, jamais
// appelée depuis l'extraction v88 (vérifié par grep : aucun call site hors de
// son propre bloc de définition). Les deux copies avaient DIVERGÉ sur
// rhHContratJours : la version morte d'AdminUI.jsx ne plafonnait pas les
// heures contractuelles à `wd` jours/semaine (bug latent, mais inerte tant
// que rien ne l'appelait). Source unique désormais = la version SecRH.jsx
// (correcte), reprise telle quelle. Supprimée d'AdminUI.jsx, plus jamais
// dupliquée nulle part — y compris le 3ᵉ calcul inline de TaskForm.jsx
// (badge déficit mensuel du planning), qui AVAIT le même bug que la version
// morte d'AdminUI.jsx et l'avait, lui, réellement en production (voir diff
// TaskForm.jsx du même commit).
export function rhWeekDays(mon){const days=[];const d=new Date(mon+'T00:00:00');for(let i=0;i<7;i++){const dd=new Date(d);dd.setDate(d.getDate()+i);days.push(iD(dd));}return days;}
export function rhGetMon(dateStr){const dt=new Date(dateStr+'T00:00:00');const day=dt.getDay();const diff=day===0?-6:1-day;dt.setDate(dt.getDate()+diff);return iD(dt);}
export function rhHEffJours(sid,jours,tc){return jours.reduce((acc,d)=>{const tc_=tc.find(x=>x.sid===sid&&x.date===d);const ms=totalWorkMs(tc_);return acc+(ms!=null?ms:0);},0)/3600000;}
export function rhHPlanJours(sid,jours,st){return st.filter(t=>t.sid===sid&&jours.includes(t.date)).reduce((acc,t)=>acc+(Number(t.dm)||0),0)/60;}
export function rhHAbsJours(s,jours,leave){
  let hAbs=0;const details=[];
  const absApproved=(leave||[]).filter(l=>l.sid===s.id&&l.st==='approved');
  jours.forEach(d=>{
    const ab=absApproved.find(l=>{
      if(l.type==='demi'||l.type==='recup')return l.sd===d;
      if(l.type==='repos'||l.type==='indispo')return false;
      if(l.type==='conge'||l.type==='maladie')return d>=l.sd&&d<=l.ed;
      return false;
    });
    if(!ab)return;
    // Étape 1B : la base horaire est celle du contrat EN VIGUEUR CE JOUR-LÀ,
    // plus les conditions actuelles de la fiche. Un congé pris sous un CDD à
    // 24h/sem ne doit pas être revalorisé parce que le salarié est repassé
    // depuis en CDI à 35h.
    // ⚠️ Changement de comportement assumé : une absence saisie HORS de toute
    // période de contrat compte désormais 0h (avant : elle était valorisée,
    // rhHAbsJours ne bornait pas sur hire/endDate). C'est la correction d'un
    // vrai défaut, pas un effet de bord — signalé à Claire.
    const c=contratActif(s,d);
    const wd=Number(c?.wd)||5;const hw=Number(c?.hw)||0;const hJour=wd>0?hw/wd:0;
    const h=(ab.type==='demi'||ab.type==='recup')?hJour/2:hJour;
    hAbs+=h;details.push({type:ab.type,malType:ab.malType,date:d,h,id:ab.id});
  });
  return{hAbs,details};
}
export function rhHContratJours(s,jours){
  // Étape 1B : plus de wd/hw lus une fois pour toutes sur la fiche. Pour
  // chaque jour, on cherche le contrat en vigueur À CETTE DATE ; aucun
  // contrat (avant embauche, après rupture, trou entre deux contrats) = 0h.
  // Le plafond `wd` jours/semaine est appliqué PAR SEMAINE **ET PAR CONTRAT** :
  // une semaine à cheval sur une rupture ne cumule pas les deux plafonds sur
  // les mêmes jours, chaque contrat plafonne ses propres jours.
  const groups={};
  jours.forEach(d=>{
    const c=contratActif(s,d);
    if(!c)return;
    const dt=new Date(d+'T00:00:00');
    const dow=dt.getDay(); // 0=dim,1=lun..6=sam
    if(dow===0||dow===6)return; // week-end : jamais contractuel
    const wd=Number(c.wd)||5;const hw=Number(c.hw)||0;
    const k=rhGetMon(d)+'|'+(c.id||'?');
    if(!groups[k])groups[k]={n:0,wd,hJour:wd>0?hw/wd:0};
    groups[k].n++;
  });
  return Object.values(groups).reduce((acc,g)=>acc+Math.min(g.n,g.wd)*g.hJour,0);
}
// ══════════════ PLANNING — Créneau extensible (demande Claire, 16/08/2026) ══════════════
// Calcule jusqu'à quelle heure la journée d'un salarié peut être étendue sans
// dépasser son quota contractuel du JOUR (hw/wd du contrat en vigueur à cette
// date), en tenant compte d'une pause : si un trou d'au moins PAUSE_SEUIL_MIN
// existe déjà entre deux tâches planifiées ce jour-là, la pause est considérée
// comme déjà couverte ; sinon PAUSE_DEFAUT_MIN est ajoutée avant le temps de
// travail restant. Fonction PURE, aucune lecture TT/noBid : TOUTES les tâches
// du jour comptent (une garde check-in occupe autant le temps qu'un ménage).
// `tasks` : tâches du salarié à CETTE date, NON filtrées par le filtre
// d'affichage Bien/Propriétaire — même principe que minutesJour/minutesSemaine
// (TaskForm.jsx, v132) : un calcul de charge réelle ne doit jamais dépendre
// d'un filtre resté actif par mégarde.
// Renvoie null si rien à afficher (pas de contrat actif, aucune tâche ce
// jour-là, ou quota déjà atteint/dépassé) — sinon {finMin,finLabel,restantMin,
// pauseCouverte,pauseAjoutee}.
export const PAUSE_SEUIL_MIN=30;   // trou mini (min) entre deux tâches pour compter comme pause déjà prise
export const PAUSE_DEFAUT_MIN=60;  // pause forfaitaire (min) ajoutée s'il n'existe aucun trou suffisant
export function travailPossibleJusqua(s,dateStr,tasks){
  const c=contratActif(s,dateStr);
  if(!c)return null;
  const wd=Number(c.wd)||0,hw=Number(c.hw)||0;
  if(wd<=0||hw<=0)return null;
  const hJourMin=Math.round(hw/wd*60);
  if(hJourMin<=0)return null;
  const jour=(tasks||[]).filter(t=>t.st!=='cancelled').sort((a,b)=>(a.sh*60+a.sm)-(b.sh*60+b.sm));
  if(!jour.length)return null;
  const schedMin=jour.reduce((acc,t)=>acc+(Number(t.dm)||0),0);
  if(schedMin>=hJourMin)return null;
  let pauseCouverte=false;
  for(let i=1;i<jour.length;i++){
    const finPrec=jour[i-1].sh*60+jour[i-1].sm+(Number(jour[i-1].dm)||0);
    const debutSuiv=jour[i].sh*60+jour[i].sm;
    if(debutSuiv-finPrec>=PAUSE_SEUIL_MIN){pauseCouverte=true;break;}
  }
  const last=jour[jour.length-1];
  const finTaches=last.sh*60+last.sm+(Number(last.dm)||0);
  const pauseAjoutee=pauseCouverte?0:PAUSE_DEFAUT_MIN;
  const restantMin=hJourMin-schedMin;
  // Arrondi supérieur à l'heure pile, TOUJOURS, + 1h de marge ajoutée par-dessus (demande Claire,
  // 16/08/2026) : certains salariés ont un quota hebdomadaire non multiple d'heures rondes, ce qui
  // donnait des horaires du type 16h45 — jugé trop précis/engageant pour un badge purement indicatif.
  // Seul l'affichage (finMin/finLabel) est arrondi ; restantMin reste exact (tooltip admin).
  const finMinBrut=finTaches+pauseAjoutee+restantMin;
  const finMin=Math.ceil(finMinBrut/60)*60+60;
  const hh=Math.floor(finMin/60)%24,mm=finMin%60;
  const finLabel=`${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
  return{finMin,finLabel,restantMin,pauseCouverte,pauseAjoutee};
}
export function rhWeeksInMonth(y,m){
  const firstDay=new Date(y,m,1);const lastDay=new Date(y,m+1,0);const mons=new Set();
  const d=new Date(firstDay);const dow=d.getDay();const diff=dow===0?-6:1-dow;d.setDate(d.getDate()+diff);
  while(d<=lastDay){mons.add(iD(d));d.setDate(d.getDate()+7);}
  return Array.from(mons);
}
export function rhWeekDaysInMonth(mon,y,m){
  const days=rhWeekDays(mon);
  const firstD=`${y}-${String(m+1).padStart(2,'0')}-01`;
  const lastD=iD(new Date(y,m+1,0));
  return days.filter(d=>d>=firstD&&d<=lastD);
}
export function rhSemaineStat(s,mon,tc,st,leave){
  const jours=rhWeekDays(mon);
  const hC=rhHContratJours(s,jours);
  const hP=rhHPlanJours(s.id,jours,st);
  const hE=rhHEffJours(s.id,jours,tc);
  const{hAbs,details}=rhHAbsJours(s,jours,leave);
  return{hC,hP,hE,hAbs,absDetails:details,ecart:hE+hAbs-hC,jours};
}
export function rhMoisCumulStat(s,y,m,tc,st,leave,todayS){
  const firstD=`${y}-${String(m+1).padStart(2,'0')}-01`;
  const lastD_all=iD(new Date(y,m+1,0));const lastD=lastD_all<todayS?lastD_all:todayS;
  const jours=[];const d=new Date(firstD+'T00:00:00');
  while(iD(d)<=lastD){jours.push(iD(d));d.setDate(d.getDate()+1);}
  const hC=rhHContratJours(s,jours);const hP=rhHPlanJours(s.id,jours,st);const hE=rhHEffJours(s.id,jours,tc);
  const{hAbs}=rhHAbsJours(s,jours,leave);const ecart=hE+hAbs-hC;
  const joursRestants=[];const df=new Date(lastD+'T00:00:00');df.setDate(df.getDate()+1);const lastDom=new Date(y,m+1,0);
  while(df<=lastDom){joursRestants.push(iD(df));df.setDate(df.getDate()+1);}
  return{hC,hP,hE,hAbs,ecart,hCRestant:rhHContratJours(s,joursRestants),hPRestant:rhHPlanJours(s.id,joursRestants,st),joursRestants};
}

// ══════════════ RAPPELS PÉRIODIQUES SUR ITEMS FACTURABLES (bien.bf[], v145) ══════════════
// Générique par conception (pas spécifique à un client/item précis) : n'importe quel item
// facturable d'un bien peut être marqué `rappelOn:true` + `rappelFreq` pour être suivi ici.
// Repose entièrement sur l'historique des rapports déjà soumis (rep7) — aucune nouvelle
// donnée de suivi à créer, aucune migration.
export const RAPPEL_FREQS={semaine:'Chaque semaine',mois:'Chaque mois',trimestre:'Chaque trimestre'};

// Clé de période courante pour une date donnée (YYYY-Www / YYYY-MM / YYYY-Tn)
export function periodeKey(freq,dateStr){
  if(!dateStr)return null;
  if(freq==='semaine'){
    const d=new Date(dateStr+'T00:00:00');
    d.setHours(0,0,0,0);d.setDate(d.getDate()+3-((d.getDay()+6)%7)); // jeudi de la semaine ISO
    const w1=new Date(d.getFullYear(),0,4);
    const wk=1+Math.round(((d-w1)/86400000-3+((w1.getDay()+6)%7))/7);
    return `${d.getFullYear()}-W${String(wk).padStart(2,'0')}`;
  }
  if(freq==='trimestre'){
    const d=new Date(dateStr+'T00:00:00');
    return `${d.getFullYear()}-T${Math.floor(d.getMonth()/3)+1}`;
  }
  return dateStr.slice(0,7); // 'mois' (défaut)
}

// Dernier rapport soumis qui a coché/quantifié cet item facturable pour ce bien.
// Type 'b' (case) : bill[fieldId]===true. Type 'n' (quantité) : Number(bill[fieldId])>0.
export function derniereVerifBf(rep,bid,fieldId){
  const matches=(rep||[]).filter(r=>r.bid===bid&&r.sub&&r.bill&&(r.bill[fieldId]===true||Number(r.bill[fieldId])>0));
  if(!matches.length)return null;
  return matches.reduce((max,r)=>r.sub>max.sub?r:max);
}

// La période courante (ex. ce mois-ci) est-elle déjà couverte pour ce bien+item ?
export function estAJourBf(rep,bid,fieldId,freq,todayS){
  const last=derniereVerifBf(rep,bid,fieldId);
  if(!last)return false;
  return periodeKey(freq,last.sub.slice(0,10))===periodeKey(freq,todayS);
}

// Statut d'un item suivi pour la période courante :
// - 'fait'     : rapport soumis avec l'item coché/quantifié sur la période (comme avant)
// - 'programme': Claire a coché manuellement "programmé" pour cette période précise (ex. elle a
//                posé l'Alerte plein écran sur un ménage à venir) — case à cocher, PAS de détection
//                automatique du lien alerte↔item (fragile, texte libre). Se réinitialise tout seul
//                à la période suivante puisqu'on compare `field.rappelProgrammePeriode` à la
//                période courante — aucun code de purge à écrire.
// - 'retard'   : ni fait, ni programmé.
export function statutRappelBf(rep,bid,field,freq,todayS){
  if(estAJourBf(rep,bid,field.id,freq,todayS))return'fait';
  if(field.rappelProgrammePeriode&&field.rappelProgrammePeriode===periodeKey(freq,todayS))return'programme';
  return'retard';
}

// Liste, tous biens confondus, des items facturables suivis (rappelOn) et pas encore FAITS sur
// la période courante (statut 'programme' ou 'retard') — pour le bandeau Dashboard. Un item sans
// rappelOn est ignoré (défaut inchangé, rétrocompat totale sur les biens/items existants).
export function rappelsBfEnRetard(biens,rep,todayS){
  const out=[];
  (biens||[]).forEach(b=>{
    if(!b.bfOn||!Array.isArray(b.bf))return;
    b.bf.forEach(field=>{
      if(!field.rappelOn)return;
      const freq=field.rappelFreq||'mois';
      const statut=statutRappelBf(rep,b.id,field,freq,todayS);
      if(statut==='fait')return;
      const last=derniereVerifBf(rep,b.id,field.id);
      out.push({bien:b,field,freq,derniere:last,statut});
    });
  });
  return out;
}
